'use client';

import { createContext, useContext, ReactNode, useEffect } from 'react';
import { analyticsService } from '../../services/AnalyticsService';

interface PageContextType {
  pageTemplate: string; // Normalized page identifier (e.g., 'newsroom', 'safety')
  locale: string; // Current locale (e.g., 'en', 'zh-hant')
}

const PageContext = createContext<PageContextType | null>(null);

export interface PageContextProviderProps extends PageContextType {
  children: ReactNode;
}

/**
 * PageContextProvider - Provides page-level context for analytics
 *
 * This provider automatically injects page_template and content_language
 * into all analytics events, eliminating the need for prop drilling.
 *
 * Registers page context with AnalyticsService on mount and updates.
 *
 * @example
 * ```tsx
 * // In app layout or page component
 * <PageContextProvider pageTemplate="newsroom" locale="en">
 *   <YourPageContent />
 * </PageContextProvider>
 * ```
 */
export const PageContextProvider = ({
  children,
  pageTemplate,
  locale,
}: PageContextProviderProps) => {
  useEffect(() => {
    analyticsService.setPageContext(pageTemplate, locale);
  }, [pageTemplate, locale]);

  return <PageContext.Provider value={{ pageTemplate, locale }}>{children}</PageContext.Provider>;
};

/**
 * usePageContext - Hook to access page context in components
 *
 * Returns page template and locale for automatic injection into analytics events.
 * Provides safe defaults if context is not available.
 *
 * @returns {PageContextType} Page template and locale
 *
 * @example
 * ```tsx
 * const { pageTemplate, locale } = usePageContext();
 * // pageTemplate: 'newsroom', locale: 'en'
 * ```
 */
export const usePageContext = (): PageContextType => {
  const context = useContext(PageContext);
  if (!context) {
    // Provide safe defaults if context is not available
    // This prevents errors during SSR or when provider is missing
    return {
      pageTemplate: 'unknown',
      locale: 'en',
    };
  }
  return context;
};
